import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  LinearProgress,
  IconButton,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack,
  ArrowForward,
  CloudUpload,
  Close,
  Restaurant as RestaurantIcon,
  Star,
  StarBorder,
  CheckCircle,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import defaultConfig from '../../config';

// ── Step definitions ─────────────────────────────────────────────────────────
// Five steps, mirroring HotelSetupWizard's step model. The wizard always
// renders the full progress bar so the user knows where they are.
const STEPS = [
  { icon: '🍽️', label: 'Profile', desc: 'Restaurant name, logo & contact' },
  { icon: '🏷️', label: 'Departments', desc: 'Kitchen / Bar / Bakery' },
  { icon: '🪑', label: 'Areas', desc: 'Indoor / Outdoor / Rooftop' },
  { icon: '📚', label: 'Categories', desc: 'Starter / Main / Dessert' },
  { icon: '🥘', label: 'Items', desc: 'Menu items with prices' },
];

// localStorage key for the draft — scoped per company so two companies on the
// same browser don't collide. Auto-saved on every form mutation.
const DRAFT_KEY_PREFIX = 'restaurantWizardDraft_v1';
const draftKey = (companyId) => `${DRAFT_KEY_PREFIX}_${companyId || 'anon'}`;

const C = {
  primary: '#4361EE',
  primaryDark: '#3350D6',
  text: '#1C1F2E',
  muted: '#64748B',
  border: '#E2E8F0',
  bg: '#F4F6FB',
  card: '#FFFFFF',
  success: '#10B981',
  warn: '#F59E0B',
  danger: '#EF4444',
};

const blankForm = () => ({
  // Step 1 — Profile
  restaurantName: '',
  starRating: 4,
  logoFile: null,
  logoPreview: null,
  description: '',
  gstNumber: '',
  foodLicenseNumber: '',
  mobile: '',
  receptionNo: '',
  email: '',
  address: '',
  state: '',
  city: '',
  cityCode: '0', // field removed from the wizard — always sent as 0
  pinCode: '',
  mapLocation: '',
  bankName: '',
  bankAddress: '',
  bankAccountNumber: '',
  ifsc: '',
  currency: 'INR',

  // Server-side id captured after Step 1 saves
  savedHotelId: null,

  // Step 2-5 buffers — populated in Phase C/D. Kept here so the draft
  // persists across reloads.
  departments: [],
  areas: [],
  categories: [],
  items: [],
});

const isFilled = (v) => v != null && String(v).trim() !== '';

export default function RestaurantSetupWizard({
  apiBaseUrl = defaultConfig.API_BASE_URL,
  getToken = () => sessionStorage.getItem('token'),
  getCompanyId = () => sessionStorage.getItem('companyId'),
  onComplete = null,
  onSkip = null,
  brandName = 'Dine In',
}) {
  const companyId = getCompanyId();
  const logoInputRef = useRef(null);

  const [phase, setPhase] = useState('wizard'); // 'wizard' | 'success'
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [saving, setSaving] = useState(false);

  // Hydrate from draft once on mount.
  const [form, setForm] = useState(() => {
    try {
      const raw = localStorage.getItem(draftKey(companyId));
      if (raw) {
        const parsed = JSON.parse(raw);
        // Files can't survive JSON serialization; nuke any stale logo refs.
        return {
          ...blankForm(),
          ...parsed,
          logoFile: null,
        };
      }
    } catch (_) {}
    return blankForm();
  });

  const upd = (field, val) =>
    setForm((f) => {
      const next = { ...f, [field]: val };
      return next;
    });

  // Auto-save draft to localStorage on form mutation (debounced lightly).
  useEffect(() => {
    const id = setTimeout(() => {
      try {
        // Strip non-serializable file blob before persisting.
        const { logoFile, ...rest } = form;
        localStorage.setItem(draftKey(companyId), JSON.stringify(rest));
      } catch (_) {}
    }, 250);
    return () => clearTimeout(id);
  }, [form, companyId]);

  // Warn the user before they refresh / close the tab while the wizard is
  // still in progress. Browsers ignore the custom message and show their own
  // generic "changes you made may not be saved" dialog — that's enough to
  // stop accidental F5 from losing context.
  useEffect(() => {
    if (phase === 'success') return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [phase]);

  // Prefill from companyDetails (mirrors HotelSetupWizard's behaviour) so
  // users don't retype info that's already in session.
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (prefilled) return;
    const pick = (d, ...keys) => {
      for (const k of keys) {
        const v = d?.[k];
        if (v != null && String(v).trim() !== '') return String(v).trim();
      }
      return '';
    };
    const applyCompany = (d) => {
      if (!d) return;
      setForm((f) => ({
        ...f,
        restaurantName:
          f.restaurantName || pick(d, 'name', 'hotelName', 'HotelName'),
        email: f.email || pick(d, 'email', 'Email'),
        mobile: f.mobile || pick(d, 'mobile', 'Mobile'),
        address:
          f.address || pick(d, 'address1', 'address', 'Address1', 'Address'),
        city: f.city || pick(d, 'city', 'City'),
        state: f.state || pick(d, 'state', 'State'),
        pinCode:
          f.pinCode || pick(d, 'pincode', 'Pincode', 'pinCode', 'PinCode'),
        currency: f.currency || pick(d, 'currency', 'Currency') || 'INR',
        gstNumber: f.gstNumber || pick(d, 'gstNumber', 'GstNumber'),
      }));
    };
    // 1) Instant prefill from the session copy.
    try {
      const raw = sessionStorage.getItem('companyDetails');
      if (raw) applyCompany(JSON.parse(raw));
    } catch (_) {}
    // 2) Fetch the FULL, fresh company record so address/city/state/pincode are
    //    complete even when the session copy is light (mirrors HotelSetupWizard).
    (async () => {
      try {
        const companyId = getCompanyId();
        const token = getToken();
        if (companyId && token) {
          const res = await fetch(
            `${apiBaseUrl}/account/GetCompanyDetails/${encodeURIComponent(companyId)}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (res.ok) {
            const d = await res.json();
            applyCompany(d);
            sessionStorage.setItem('companyDetails', JSON.stringify(d));
          }
        }
      } catch (_) {}
    })();
    setPrefilled(true);
  }, [prefilled]);

  // ── Validation per step ────────────────────────────────────────────────────
  // Returns first error message, or empty string when valid. Drives the
  // disabled state on the Next button + an inline error chip.
  const validateStep = (idx) => {
    if (idx === 0) {
      if (!isFilled(form.restaurantName)) return 'Restaurant name is required';
      if (!isFilled(form.description)) return 'Description is required';
      if (!isFilled(form.mobile)) return 'Mobile number is required';
      if (!isFilled(form.email)) return 'Email is required';
      if (!/^\S+@\S+\.\S+$/.test(form.email.trim()))
        return 'Email looks invalid';
      if (!isFilled(form.mapLocation)) return 'Google Map link is required';
      if (!isFilled(form.address)) return 'Address is required';
      if (!isFilled(form.city)) return 'City is required';
      if (!isFilled(form.state)) return 'State is required';
      if (!isFilled(form.pinCode)) return 'Pincode is required';
    }
    if (idx === 1) {
      if (!form.departments?.length) return 'Add at least one department';
    }
    if (idx === 2) {
      if (!form.areas?.length) return 'Add at least one area';
    }
    if (idx === 3) {
      if (!form.categories?.length) return 'Add at least one category';
    }
    if (idx === 4) {
      if (!form.items?.length) return 'Add at least one item';
    }
    return '';
  };


  // ── Step 1 save: POST /Home/SaveHotel with default-zeroed hotel fields ───
  // Returns the new HotelMaster id on success so the later steps can attach
  // departments / areas / categories / items to the correct hotel.
  const saveProfile = async () => {
    if (form.savedHotelId) return form.savedHotelId; // already saved
    const token = getToken();
    if (!token) {
      throw new Error('Authentication token missing. Please log in again.');
    }
    const fd = new FormData();
    fd.append('Name', form.restaurantName.trim());
    fd.append('Rating', String(form.starRating || 0));
    fd.append('Address', form.address.trim());
    fd.append('City', form.city.trim());
    fd.append('CityCode', form.cityCode?.trim() || '0');
    fd.append('Pincode', form.pinCode.trim());
    fd.append('State', form.state.trim());
    fd.append('Mobile', form.mobile.trim());
    fd.append('Email', form.email.trim());
    fd.append('MapLocation', form.mapLocation.trim());
    fd.append('Location', form.mapLocation.trim());
    fd.append('Description', form.description.trim());
    fd.append('Currency', form.currency || 'INR');
    fd.append('CompanyId', companyId || '');
    if (form.logoFile) fd.append('HotelLogo', form.logoFile);
    if (isFilled(form.gstNumber)) fd.append('GstNumber', form.gstNumber.trim());
    if (isFilled(form.foodLicenseNumber))
      fd.append('FoodLicenseNumber', form.foodLicenseNumber.trim());
    if (isFilled(form.receptionNo))
      fd.append('Riceptionno', form.receptionNo.trim());
    if (isFilled(form.bankName)) fd.append('BankName', form.bankName.trim());
    if (isFilled(form.bankAddress))
      fd.append('BankAddress', form.bankAddress.trim());
    if (isFilled(form.bankAccountNumber))
      fd.append('BankAccountNumber', form.bankAccountNumber.trim());
    if (isFilled(form.ifsc)) fd.append('IFSC', form.ifsc.trim());

    // Restaurant flag + default-zeroed hotel fields. The backend ignores
    // Rooms/Amenities/Attractions/Images when these are empty so the
    // existing hotel save path stays untouched.
    fd.append('IsRestaurant', 'true');
    fd.append('FloorCount', '0');
    fd.append('Is24Hour', 'false');
    fd.append('CheckInTime', '');
    fd.append('CheckOutTime', '');
    // Was hardcoded 'India'. Keep India for INR; otherwise use the company's
    // real country so it can never contradict the tax profile set below.
    fd.append(
      'Country',
      (form.currency || 'INR') === 'INR'
        ? 'India'
        : (() => {
            try {
              const cd = JSON.parse(
                sessionStorage.getItem('companyDetails') || '{}',
              );
              return cd?.country || '';
            } catch (_) {
              return '';
            }
          })(),
    );

    const res = await fetch(`${apiBaseUrl}/Home/SaveHotel`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `SaveHotel failed (${res.status})`);
    }

    // ---- Company tax profile ------------------------------------------
    // GST is India-only. This wizard has no country field, so India is
    // inferred from the currency (same fallback the hotel wizard uses:
    // country === 'india' || currency === 'INR'). A non-Indian restaurant
    // defaults to No-Tax; the backend flips CompanyMaster.GstApplicable from
    // TaxMode, turning GST off across the whole app (orders, KOT, bills).
    // Best-effort: never block the restaurant from saving.
    try {
      const isIndia = (form.currency || 'INR') === 'INR';
      const taxProfile = isIndia
        ? { TaxMode: 'GST', TaxRate: 18 }
        : { TaxMode: 'None', TaxRate: 0 };
      const taxRes = await fetch(`${apiBaseUrl}/account/CompanyTaxProfile`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ CompanyId: Number(companyId), ...taxProfile }),
      });
      if (taxRes.ok) {
        // Keep this session in step so the UI hides GST immediately.
        const cd = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
        cd.taxMode = taxProfile.TaxMode;
        cd.taxRate = taxProfile.TaxRate;
        cd.gstApplicable = isIndia;
        sessionStorage.setItem('companyDetails', JSON.stringify(cd));
      }
    } catch (e) {
      console.warn('[RestaurantWizard] tax profile save skipped:', e);
    }
    const data = await res.json().catch(() => ({}));
    let newId = data?.hotelId ?? data?.id ?? data?.Id ?? data?.HotelId ?? null;

    // Fallback — fetch the latest hotel for this company if the response
    // didn't echo the new id (older SaveHotel returns only { message }).
    if (!newId) {
      try {
        const listRes = await fetch(
          `${apiBaseUrl}/Home/GetHotels/${encodeURIComponent(companyId)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (listRes.ok) {
          const hotels = await listRes.json().catch(() => []);
          if (Array.isArray(hotels) && hotels.length > 0) {
            const latest = hotels[hotels.length - 1];
            newId = latest?.id ?? latest?.Id ?? null;
          }
        }
      } catch (_) {}
    }
    if (!newId) throw new Error('Saved but could not resolve hotel id.');
    upd('savedHotelId', newId);
    return newId;
  };

  // ── Step 2 save: POST one /Home/AddDepartment per un-synced row ────────────
  // Departments persist as separate records (Departmentmaster). We only push
  // rows the user hasn't already synced so going Back→Next is idempotent.
  const saveDepartments = async () => {
    const token = getToken();
    if (!token) throw new Error('Authentication token missing.');
    const pending = (form.departments || []).filter((d) => !d._synced);
    if (!pending.length) return;
    const results = await Promise.allSettled(
      pending.map((d) => {
        const fd = new FormData();
        fd.append('Department', d.name.trim());
        fd.append('Printername', (d.printer || d.name).trim());
        fd.append('Mobile', (d.mobile || '').trim());
        fd.append('Companyid', companyId || '');
        return fetch(`${apiBaseUrl}/Home/AddDepartment`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
      }),
    );
    // Mark each row as synced on a 2xx, leave failures un-synced so Next can
    // retry them without duplicating successful inserts.
    const updated = [...(form.departments || [])];
    let idx = 0;
    for (let i = 0; i < updated.length; i += 1) {
      if (updated[i]._synced) continue;
      const r = results[idx++];
      if (r.status === 'fulfilled' && r.value.ok) {
        updated[i] = { ...updated[i], _synced: true };
      }
    }
    upd('departments', updated);
    const failures = results.filter(
      (r) => r.status !== 'fulfilled' || !r.value.ok,
    ).length;
    if (failures > 0)
      throw new Error(`${failures} department(s) failed to save.`);
  };

  // ── Step 4 save: POST one /Home/CreateCategory per un-synced row, then
  // refetch GET /Home/GetAllCategory/{companyId} to resolve each new row's
  // server id (CreateCategory's response doesn't echo it). Server ids are
  // needed for Step 5 — items POST CategoryId, not name.
  const saveCategories = async () => {
    const token = getToken();
    if (!token) throw new Error('Authentication token missing.');
    const list = form.categories || [];
    const pending = list.filter((c) => !c._synced);
    if (pending.length) {
      const results = await Promise.allSettled(
        pending.map((c) => {
          const fd = new FormData();
          fd.append('CategoryName', c.name.trim());
          fd.append('CompanyId', companyId || '');
          if (c.imageFile) fd.append('CategoryImage', c.imageFile);
          return fetch(`${apiBaseUrl}/Home/CreateCategory`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });
        }),
      );
      const next = [...list];
      let idx = 0;
      for (let i = 0; i < next.length; i += 1) {
        if (next[i]._synced) continue;
        const r = results[idx++];
        if (r.status === 'fulfilled' && r.value.ok) {
          next[i] = { ...next[i], _synced: true };
        }
      }
      upd('categories', next);
      const failures = results.filter(
        (r) => r.status !== 'fulfilled' || !r.value.ok,
      ).length;
      if (failures > 0)
        throw new Error(`${failures} category(s) failed to save.`);
    }

    // Resolve server ids by refetching and matching on name. Items in Step 5
    // need these ids — without them the item save will fail.
    try {
      const res = await fetch(
        `${apiBaseUrl}/Home/GetAllCategory/${encodeURIComponent(companyId || '')}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        const rows = await res.json().catch(() => []);
        const byName = new Map();
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          const n = String(
            row.category_name || row.categoryName || row.name || '',
          )
            .trim()
            .toLowerCase();
          const id = row.id ?? row.Id ?? row.categoryId ?? null;
          if (n && id != null) byName.set(n, id);
        });
        upd(
          'categories',
          (form.categories || []).map((c) => {
            if (c.serverId) return c;
            const id = byName.get(String(c.name).trim().toLowerCase());
            return id != null ? { ...c, serverId: id } : c;
          }),
        );
      }
    } catch (_) {
      /* non-blocking — Step 5 will surface a friendlier error if ids missing */
    }
  };

  // ── Step 5 save: POST one /Home/CreateItem per un-synced row. Resolves
  // each item's CategoryId from the local categories array (must have been
  // saved in Step 4). Department is sent by name string per the DTO.
  const saveItems = async () => {
    const token = getToken();
    if (!token) throw new Error('Authentication token missing.');
    const list = form.items || [];
    const pending = list.filter((it) => !it._synced);
    if (!pending.length) return;

    // Build a name→serverId lookup so items can reference categories by id.
    const catById = new Map();
    (form.categories || []).forEach((c) => {
      if (c.serverId != null) {
        catById.set(c.name.trim().toLowerCase(), c.serverId);
      }
    });

    const results = await Promise.allSettled(
      pending.map((it) => {
        const catId =
          it.categoryServerId ??
          catById.get(
            String(it.categoryName || '')
              .trim()
              .toLowerCase(),
          ) ??
          null;
        if (catId == null) {
          // Reject this row early so the failure count is accurate.
          return Promise.resolve({ ok: false, _missingCat: true });
        }
        const payload = {
          CategoryId: Number(catId),
          ItemName: it.name.trim(),
          HsnCode: it.hsn || '',
          Department: it.department || '',
          itemPrice: Number(it.price) || 0,
          gst: Number(it.tax) || 0,
          VatRate: 0,
          IsVatApplicable: false,
          companyid: Number(companyId) || 0,
        };
        return fetch(`${apiBaseUrl}/Home/CreateItem`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      }),
    );
    const next = [...list];
    let idx = 0;
    for (let i = 0; i < next.length; i += 1) {
      if (next[i]._synced) continue;
      const r = results[idx++];
      if (r.status === 'fulfilled' && r.value.ok) {
        next[i] = { ...next[i], _synced: true };
      }
    }
    upd('items', next);
    const failures = results.filter(
      (r) => r.status !== 'fulfilled' || !r.value.ok,
    ).length;
    if (failures > 0) throw new Error(`${failures} item(s) failed to save.`);
  };

  // ── Step 3 save: POST one /Home/AddResturentarea per un-synced row ─────────
  const saveAreas = async () => {
    const token = getToken();
    if (!token) throw new Error('Authentication token missing.');
    const pending = (form.areas || []).filter((a) => !a._synced);
    if (!pending.length) return;
    const results = await Promise.allSettled(
      pending.map((a) => {
        const fd = new FormData();
        fd.append('AreaName', a.name.trim());
        // Backend takes a single TableName string — we send a comma-joined
        // list of auto-generated table names so the user doesn't have to
        // type each one.
        const tableCount = Math.max(1, Number(a.tableCount) || 1);
        // Prefix each table with the area's first letter (Indoor → I-1, I-2;
        // Rooftop → R-1, R-2). Falls back to "T" if the area name is blank.
        const prefix = (a.name.trim()[0] || 'T').toUpperCase();
        const tableNames = Array.from(
          { length: tableCount },
          (_, i) => `${prefix}-${i + 1}`,
        ).join(',');
        fd.append('TableName', tableNames);
        fd.append('TableCount', String(tableCount));
        fd.append('Companyid', companyId || '');
        return fetch(`${apiBaseUrl}/Home/AddResturentarea`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
      }),
    );
    const updated = [...(form.areas || [])];
    let idx = 0;
    let firstError = '';
    for (let i = 0; i < updated.length; i += 1) {
      if (updated[i]._synced) continue;
      const r = results[idx++];
      if (r.status === 'fulfilled' && r.value.ok) {
        updated[i] = { ...updated[i], _synced: true };
      } else if (!firstError) {
        if (r.status === 'rejected') {
          firstError = r.reason?.message || 'Network error';
        } else {
          const body = await r.value
            .clone()
            .json()
            .catch(() => null);
          firstError =
            body?.message ||
            body?.title ||
            (await r.value.clone().text().catch(() => '')) ||
            `HTTP ${r.value.status}`;
        }
      }
    }
    upd('areas', updated);
    if (firstError) throw new Error(firstError);
  };

  // ── Navigation ─────────────────────────────────────────────────────────────
  const goNext = async () => {
    const err = validateStep(step);
    if (err) {
      toast.error(err);
      return;
    }
    // Per-step save dispatch. Each saver POSTs only un-synced rows so going
    // Back→Next is idempotent.
    const stepSavers = {
      0: saveProfile,
      1: saveDepartments,
      2: saveAreas,
      3: saveCategories,
      4: saveItems,
    };
    if (stepSavers[step]) {
      setSaving(true);
      try {
        await stepSavers[step]();
        if (step === STEPS.length - 1) {
          // Last step saved — clear draft and flip to success screen.
          // Mark setup complete so ProtectedRoute / SetupGuard stop sending
          // the user back to /setup on the next navigation.
          try {
            localStorage.removeItem(draftKey(companyId));
            localStorage.setItem('hotelSetupDone', 'true');
          } catch (_) {}
          toast.success('Restaurant setup completed successfully!');
          setPhase('success');
        } else {
          setDirection(1);
          setStep((s) => s + 1);
        }
      } catch (e) {
        toast.error(e.message || 'Could not save this step');
      } finally {
        setSaving(false);
      }
      return;
    }
    setDirection(1);
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const goBack = () => {
    setDirection(-1);
    setStep((s) => Math.max(0, s - 1));
  };

  // ── Render helpers ─────────────────────────────────────────────────────────
  const onLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm((f) => ({
        ...f,
        logoFile: file,
        logoPreview: ev.target.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  // Success screen — shown after the final step is completed.
  if (phase === 'success') {
    const itemsCount = (form.items || []).length;
    const areasCount = (form.areas || []).length;
    const categoriesCount = (form.categories || []).length;
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: C.bg,
          p: 3,
        }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 3,
            maxWidth: 540,
            width: '100%',
            textAlign: 'center',
          }}>
          <CheckCircle sx={{ fontSize: 72, color: C.success, mb: 1.5 }} />
          <Typography
            variant='h4'
            sx={{
              fontWeight: 900,
              color: C.text,
              mb: 1,
              fontFamily: '"DM Sans", sans-serif',
            }}>
            🎉 You're live!
          </Typography>
          <Typography sx={{ color: C.text, fontWeight: 600, mb: 1 }}>
            {form.restaurantName} is set up and ready to take orders.
          </Typography>
          <Typography sx={{ color: C.muted, fontSize: '0.85rem', mb: 3 }}>
            {categoriesCount} categor{categoriesCount === 1 ? 'y' : 'ies'} ·{' '}
            {itemsCount} menu item{itemsCount === 1 ? '' : 's'} · {areasCount}{' '}
            dining area{areasCount === 1 ? '' : 's'} configured.
          </Typography>
          <Button
            variant='contained'
            size='large'
            onClick={() => {
              if (typeof onComplete === 'function') {
                onComplete(form.savedHotelId);
              }
              window.location.href = '/admin/resturantdashbord';
            }}
            sx={{
              bgcolor: C.primary,
              fontWeight: 700,
              textTransform: 'none',
              px: 4,
              '&:hover': { bgcolor: C.primaryDark },
            }}>
            Go to Restaurant Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: C.bg, pb: 6 }}>
      {/* ── Sticky header with progress ── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          bgcolor: '#fff',
          borderBottom: `1px solid ${C.border}`,
          py: 1.5,
          px: { xs: 1.5, sm: 3 },
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            maxWidth: 960,
            mx: 'auto',
          }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: C.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
            <RestaurantIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1rem',
                color: C.text,
                lineHeight: 1.1,
                fontFamily: '"DM Sans", sans-serif',
              }}>
              {brandName} · Restaurant Setup
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: C.muted }}>
              Step {step + 1} of {STEPS.length} · {STEPS[step].label}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '0.7rem',
              color: C.muted,
              display: { xs: 'none', sm: 'block' },
            }}>
            {Math.round(((step + 1) / STEPS.length) * 100)}%
          </Typography>
        </Box>
        <LinearProgress
          variant='determinate'
          value={((step + 1) / STEPS.length) * 100}
          sx={{
            mt: 1.2,
            borderRadius: 3,
            height: 6,
            bgcolor: C.border,
            '& .MuiLinearProgress-bar': { bgcolor: C.primary },
            maxWidth: 960,
            mx: 'auto',
          }}
        />
      </Box>

      {/* ── Step indicator chips (clickable when validated above) ── */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          py: 1.5,
          px: { xs: 1.5, sm: 3 },
          maxWidth: 960,
          mx: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
        }}>
        {STEPS.map((s, i) => {
          const isActive = i === step;
          const isDone = i < step;
          return (
            <Box
              key={s.label}
              onClick={() => {
                // Allow jumping back to completed steps. Skipping forward is
                // gated by validation so we don't allow it here.
                if (i < step) {
                  setDirection(-1);
                  setStep(i);
                }
              }}
              sx={{
                flexShrink: 0,
                cursor: i < step ? 'pointer' : 'default',
                px: 1.5,
                py: 0.7,
                borderRadius: 2,
                fontSize: '0.78rem',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                border: `1.5px solid ${
                  isActive ? C.primary : isDone ? C.success : C.border
                }`,
                bgcolor: isActive
                  ? alpha(C.primary, 0.1)
                  : isDone
                    ? alpha(C.success, 0.1)
                    : '#fff',
                color: isActive ? C.primary : isDone ? C.success : C.muted,
              }}>
              <span>{s.icon}</span>
              <span>{s.label}</span>
            </Box>
          );
        })}
      </Box>

      {/* ── Step content (animated) ── */}
      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 1.5, sm: 3 }, pb: 9 }}>
        <AnimatePresence mode='wait' custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            initial={{ opacity: 0, x: direction > 0 ? 40 : -40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction > 0 ? -40 : 40 }}
            transition={{ duration: 0.25 }}>
            {step === 0 && (
              <ProfileStep
                form={form}
                upd={upd}
                logoInputRef={logoInputRef}
                onLogoPick={onLogoPick}
              />
            )}
            {step === 1 && <DepartmentsStep form={form} upd={upd} />}
            {step === 2 && <AreasStep form={form} upd={upd} />}
            {step === 3 && <CategoriesStep form={form} upd={upd} />}
            {step === 4 && <ItemsStep form={form} upd={upd} />}
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* ── Sticky footer with Prev / Next ── */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: '#fff',
          borderTop: `1px solid ${C.border}`,
          py: 1.2,
          px: { xs: 1.5, sm: 3 },
          zIndex: 6,
        }}>
        <Box
          sx={{
            maxWidth: 960,
            mx: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={goBack}
            disabled={step === 0 || saving}
            variant='outlined'
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
              borderColor: C.border,
              color: C.text,
              px: { xs: 2, sm: 3 },
            }}>
            Previous
          </Button>
          {typeof onSkip === 'function' && (
            <Button
              onClick={() => {
                try {
                  localStorage.removeItem(draftKey(companyId));
                } catch (_) {}
                onSkip();
              }}
              sx={{
                color: C.muted,
                textTransform: 'none',
                fontWeight: 600,
                display: { xs: 'none', sm: 'inline-flex' },
              }}>
              Skip setup
            </Button>
          )}
          <Button
            endIcon={
              saving ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <ArrowForward />
              )
            }
            onClick={goNext}
            disabled={saving}
            variant='contained'
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
              bgcolor: C.primary,
              px: { xs: 2, sm: 4 },
              '&:hover': { bgcolor: C.primaryDark },
              '&.Mui-disabled': {
                bgcolor: alpha(C.primary, 0.4),
                color: '#fff',
              },
            }}>
            {step === STEPS.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ── Step 1 — Restaurant Profile ──────────────────────────────────────────────
// Single-screen form with all of: name, rating, logo, description, GST,
// food license, mobile, reception, email, address, state, city, citycode,
// pincode, map link, bank details. Phase B target: this step is fully
// functional and saves to /Home/SaveHotel before advancing to Step 2.
function ProfileStep({ form, upd, logoInputRef, onLogoPick }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        border: `1px solid ${C.border}`,
        bgcolor: C.card,
      }}>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: '1.1rem',
          color: C.text,
          mb: 0.4,
          fontFamily: '"DM Sans", sans-serif',
        }}>
        Restaurant Profile
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', color: C.muted, mb: 2.5 }}>
        Basic details we'll print on the bill, KOT, and dashboard header.
      </Typography>

      {/* Logo + name + rating row */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Box
            onClick={() => logoInputRef.current?.click()}
            sx={{
              width: '100%',
              aspectRatio: '1 / 1',
              maxWidth: 160,
              mx: { xs: 'auto', sm: 0 },
              borderRadius: 2,
              border: `1.5px dashed ${form.logoPreview ? C.primary : C.border}`,
              bgcolor: form.logoPreview ? '#fff' : alpha(C.primary, 0.05),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 0.6,
              cursor: 'pointer',
              overflow: 'hidden',
              position: 'relative',
            }}>
            {form.logoPreview ? (
              <>
                <img
                  src={form.logoPreview}
                  alt='Logo'
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    upd('logoFile', null);
                    upd('logoPreview', null);
                  }}
                  size='small'
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  }}>
                  <Close fontSize='small' />
                </IconButton>
              </>
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 32, color: C.primary }} />
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: C.primary,
                  }}>
                  Upload Logo
                </Typography>
                <Typography sx={{ fontSize: '0.62rem', color: C.muted }}>
                  PNG / JPG · square preferred
                </Typography>
              </>
            )}
            <input
              ref={logoInputRef}
              type='file'
              accept='image/*'
              hidden
              onChange={onLogoPick}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            required
            label='Restaurant Name'
            value={form.restaurantName}
            onChange={(e) => upd('restaurantName', e.target.value)}
            placeholder='e.g. The Spice Route'
            size='small'
            sx={{ mb: 1.5 }}
          />
          <Typography sx={{ fontSize: '0.72rem', color: C.muted, mb: 0.5 }}>
            Rating
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.3 }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <IconButton
                key={n}
                size='small'
                onClick={() => upd('starRating', n)}
                sx={{ p: 0.3 }}>
                {n <= (form.starRating || 0) ? (
                  <Star sx={{ color: '#FBBF24', fontSize: 26 }} />
                ) : (
                  <StarBorder sx={{ color: C.muted, fontSize: 26 }} />
                )}
              </IconButton>
            ))}
          </Box>
        </Grid>
      </Grid>

      <TextField
        fullWidth
        required
        label='Description'
        value={form.description}
        onChange={(e) => upd('description', e.target.value)}
        placeholder='Short tagline / cuisine focus / specialty'
        size='small'
        multiline
        minRows={2}
        sx={{ mb: 2 }}
      />

      {/* Contact */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.78rem',
          color: C.text,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          mt: 1,
          mb: 1,
        }}>
        Contact
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label='Mobile'
            value={form.mobile}
            onChange={(e) => upd('mobile', e.target.value)}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label='Reception No'
            value={form.receptionNo}
            onChange={(e) => upd('receptionNo', e.target.value)}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label='Email'
            type='email'
            value={form.email}
            onChange={(e) => upd('email', e.target.value)}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label='Google Map Link'
            value={form.mapLocation}
            onChange={(e) => upd('mapLocation', e.target.value)}
            placeholder='https://maps.app.goo.gl/...'
            size='small'
          />
        </Grid>
      </Grid>

      {/* Address */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.78rem',
          color: C.text,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          mt: 1,
          mb: 1,
        }}>
        Address
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label='Address'
            value={form.address}
            onChange={(e) => upd('address', e.target.value)}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label='State'
            value={form.state}
            onChange={(e) => upd('state', e.target.value)}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label='City'
            value={form.city}
            onChange={(e) => upd('city', e.target.value)}
            size='small'
          />
        </Grid>
        <Grid item xs={6} sm={6}>
          <TextField
            fullWidth
            required
            label='Pincode'
            value={form.pinCode}
            onChange={(e) => upd('pinCode', e.target.value)}
            size='small'
          />
        </Grid>
      </Grid>

      {/* Statutory */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.78rem',
          color: C.text,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          mt: 1,
          mb: 1,
        }}>
        Statutory
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label='GST Number'
            value={form.gstNumber}
            onChange={(e) => upd('gstNumber', e.target.value.toUpperCase())}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label='Food License Number'
            value={form.foodLicenseNumber}
            onChange={(e) => upd('foodLicenseNumber', e.target.value)}
            size='small'
          />
        </Grid>
      </Grid>

      {/* Banking */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.78rem',
          color: C.text,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          mt: 1,
          mb: 1,
        }}>
        Banking
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label='Bank Name'
            value={form.bankName}
            onChange={(e) => upd('bankName', e.target.value)}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label='Bank Address'
            value={form.bankAddress}
            onChange={(e) => upd('bankAddress', e.target.value)}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label='Account Number'
            value={form.bankAccountNumber}
            onChange={(e) => upd('bankAccountNumber', e.target.value)}
            size='small'
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label='IFSC'
            value={form.ifsc}
            onChange={(e) => upd('ifsc', e.target.value.toUpperCase())}
            size='small'
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

// ── Step 2 — Departments ─────────────────────────────────────────────────────
// Departments are the KOT-routing buckets (Kitchen / Bar / Bakery / etc.).
// Each row stores name + optional printer + optional mobile. Saved rows are
// flagged with _synced so Back→Next doesn't re-insert them.
const DEPT_PRESETS = ['Kitchen', 'Bar', 'Bakery', 'Main Counter', 'Dessert'];

function DepartmentsStep({ form, upd }) {
  const [name, setName] = useState('');
  const [printer, setPrinter] = useState('');
  const [mobile, setMobile] = useState('');

  // Preset dialog: when a user taps a preset chip we ask for printer + mobile
  // before adding the row (instead of inserting it silently with blanks).
  const [presetDialog, setPresetDialog] = useState({
    open: false,
    name: '',
    printer: '',
    mobile: '',
  });

  const list = form.departments || [];

  const addRow = (n, p = '', m = '') => {
    const clean = (n || '').trim();
    if (!clean) {
      toast.error('Department name is required');
      return;
    }
    const cleanMobile = (m || '').trim();
    if (!cleanMobile) {
      toast.error('Mobile number is required');
      return;
    }
    // Dedupe by name (case-insensitive) — quick presets shouldn't double-add.
    if (
      list.some(
        (d) => String(d.name).trim().toLowerCase() === clean.toLowerCase(),
      )
    ) {
      toast.error(`"${clean}" is already added`);
      return;
    }
    // Printer falls back to "default" when the user leaves it blank.
    const cleanPrinter = (p || '').trim() || 'default';
    upd('departments', [
      ...list,
      {
        name: clean,
        printer: cleanPrinter,
        mobile: cleanMobile,
        _synced: false,
      },
    ]);
    setName('');
    setPrinter('');
    setMobile('');
  };

  const removeRow = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    upd('departments', next);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        border: `1px solid ${C.border}`,
        bgcolor: C.card,
      }}>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: '1.1rem',
          color: C.text,
          mb: 0.4,
          fontFamily: '"DM Sans", sans-serif',
        }}>
        Departments
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', color: C.muted, mb: 2 }}>
        Each department routes its own KOT slips (Kitchen, Bar, Bakery, etc.).
      </Typography>

      {/* Quick-add chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 2 }}>
        {DEPT_PRESETS.map((p) => {
          const taken = list.some(
            (d) => String(d.name).trim().toLowerCase() === p.toLowerCase(),
          );
          return (
            <Box
              key={p}
              onClick={() =>
                !taken &&
                setPresetDialog({
                  open: true,
                  name: p,
                  printer: '',
                  mobile: '',
                })
              }
              sx={{
                px: 1.4,
                py: 0.6,
                borderRadius: 20,
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: taken ? 'default' : 'pointer',
                border: `1.5px solid ${taken ? C.border : C.primary}`,
                bgcolor: taken ? alpha(C.success, 0.08) : '#fff',
                color: taken ? C.success : C.primary,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.4,
                userSelect: 'none',
                opacity: taken ? 0.85 : 1,
              }}>
              {taken ? '✓' : '+'} {p}
            </Box>
          );
        })}
      </Box>

      {/* Preset details dialog — fired when a chip above is clicked */}
      <Dialog
        open={presetDialog.open}
        onClose={() => setPresetDialog((d) => ({ ...d, open: false }))}
        maxWidth='xs'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: C.text }}>
          Add "{presetDialog.name}" department
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.78rem', color: C.muted, mb: 2 }}>
            Enter the printer and contact for this department. Both are
            optional — leave blank if not applicable.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size='small'
            label='Printer'
            value={presetDialog.printer}
            onChange={(e) =>
              setPresetDialog((d) => ({ ...d, printer: e.target.value }))
            }
            placeholder='Leave blank to use "default"'
            sx={{ mb: 1.5, mt: 0.5 }}
          />
          <TextField
            fullWidth
            required
            size='small'
            label='Mobile'
            value={presetDialog.mobile}
            onChange={(e) =>
              setPresetDialog((d) => ({ ...d, mobile: e.target.value }))
            }
            placeholder='Contact number'
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPresetDialog((d) => ({ ...d, open: false }))}
            sx={{ textTransform: 'none', color: C.muted, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              addRow(presetDialog.name, presetDialog.printer, presetDialog.mobile);
              setPresetDialog({
                open: false,
                name: '',
                printer: '',
                mobile: '',
              });
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: C.primary,
              '&:hover': { bgcolor: C.primaryDark },
            }}>
            Add Department
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom add row */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size='small'
            label='Department name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addRow(name, printer, mobile);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size='small'
            label='Printer'
            value={printer}
            onChange={(e) => setPrinter(e.target.value)}
            placeholder='Leave blank to use "default"'
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            required
            size='small'
            label='Mobile'
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            placeholder='Contact number'
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            fullWidth
            variant='outlined'
            disabled={!name.trim()}
            onClick={() => addRow(name, printer, mobile)}
            sx={{
              height: '100%',
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 700,
              borderColor: C.primary,
              color: C.primary,
            }}>
            Add
          </Button>
        </Grid>
      </Grid>

      {/* List of added departments */}
      {list.length === 0 ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(C.primary, 0.04),
            border: `1px dashed ${C.border}`,
            textAlign: 'center',
            color: C.muted,
            fontSize: '0.82rem',
          }}>
          No departments yet. Tap a preset or type one above.
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          {list.map((d, i) => (
            <Box
              key={`${d.name}-${i}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: `1px solid ${d._synced ? alpha(C.success, 0.5) : C.border}`,
                bgcolor: d._synced ? alpha(C.success, 0.05) : '#fff',
              }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.text }}>
                  {d.name}
                </Typography>
                {(d.printer || d.mobile) && (
                  <Typography
                    sx={{ fontSize: '0.7rem', color: C.muted, mt: 0.1 }}>
                    {[
                      d.printer && `Printer: ${d.printer}`,
                      d.mobile && `Mob: ${d.mobile}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                )}
              </Box>
              {d._synced && (
                <Box
                  sx={{
                    fontSize: '0.62rem',
                    fontWeight: 800,
                    color: C.success,
                    px: 0.7,
                    py: 0.15,
                    borderRadius: 0.8,
                    bgcolor: alpha(C.success, 0.12),
                    letterSpacing: 0.4,
                  }}>
                  SAVED
                </Box>
              )}
              <IconButton
                size='small'
                onClick={() => removeRow(i)}
                disabled={d._synced}
                sx={{ color: C.danger }}>
                <Close fontSize='small' />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

// ── Step 3 — Areas (dining sections + table count) ───────────────────────────
const AREA_PRESETS = [
  'Indoor',
  'Outdoor',
  'Rooftop',
  'Garden',
  'VIP',
  'Family Section',
];

function AreasStep({ form, upd }) {
  const [name, setName] = useState('');
  const [tableCount, setTableCount] = useState(4);

  const list = form.areas || [];

  const addRow = (n, count = 4) => {
    const clean = (n || '').trim();
    if (!clean) {
      toast.error('Area name is required');
      return;
    }
    if (
      list.some(
        (a) => String(a.name).trim().toLowerCase() === clean.toLowerCase(),
      )
    ) {
      toast.error(`"${clean}" is already added`);
      return;
    }
    upd('areas', [
      ...list,
      {
        name: clean,
        tableCount: Math.max(1, Number(count) || 1),
        _synced: false,
      },
    ]);
    setName('');
    setTableCount(4);
  };

  const bumpCount = (idx, delta) => {
    const next = list.map((a, i) =>
      i === idx
        ? {
            ...a,
            tableCount: Math.max(1, (Number(a.tableCount) || 1) + delta),
          }
        : a,
    );
    upd('areas', next);
  };

  const removeRow = (idx) => {
    upd(
      'areas',
      list.filter((_, i) => i !== idx),
    );
  };

  const totalTables = list.reduce(
    (sum, a) => sum + (Number(a.tableCount) || 0),
    0,
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        border: `1px solid ${C.border}`,
        bgcolor: C.card,
      }}>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: '1.1rem',
          color: C.text,
          mb: 0.4,
          fontFamily: '"DM Sans", sans-serif',
        }}>
        Dining Areas
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', color: C.muted, mb: 2 }}>
        Split the restaurant into seating sections. Table names auto-generate
        per area using its first letter — Indoor → I-1, I-2; Rooftop → R-1, R-2.
      </Typography>

      {/* Quick-add chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 2 }}>
        {AREA_PRESETS.map((p) => {
          const taken = list.some(
            (a) => String(a.name).trim().toLowerCase() === p.toLowerCase(),
          );
          return (
            <Box
              key={p}
              onClick={() => !taken && addRow(p, 4)}
              sx={{
                px: 1.4,
                py: 0.6,
                borderRadius: 20,
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: taken ? 'default' : 'pointer',
                border: `1.5px solid ${taken ? C.border : C.primary}`,
                bgcolor: taken ? alpha(C.success, 0.08) : '#fff',
                color: taken ? C.success : C.primary,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.4,
                userSelect: 'none',
                opacity: taken ? 0.85 : 1,
              }}>
              {taken ? '✓' : '+'} {p}
            </Box>
          );
        })}
      </Box>

      {/* Custom area + table count */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size='small'
            label='Area name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addRow(name, tableCount);
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            size='small'
            type='number'
            label='Tables'
            value={tableCount}
            onChange={(e) => setTableCount(e.target.value)}
            inputProps={{ min: 1, max: 200 }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Button
            fullWidth
            variant='outlined'
            disabled={!name.trim()}
            onClick={() => addRow(name, tableCount)}
            sx={{
              height: '100%',
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 700,
              borderColor: C.primary,
              color: C.primary,
            }}>
            Add
          </Button>
        </Grid>
      </Grid>

      {/* List */}
      {list.length === 0 ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(C.primary, 0.04),
            border: `1px dashed ${C.border}`,
            textAlign: 'center',
            color: C.muted,
            fontSize: '0.82rem',
          }}>
          No areas yet. Tap a preset or type one above.
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          {list.map((a, i) => (
            <Box
              key={`${a.name}-${i}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: `1px solid ${a._synced ? alpha(C.success, 0.5) : C.border}`,
                bgcolor: a._synced ? alpha(C.success, 0.05) : '#fff',
              }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.text }}>
                  {a.name}
                </Typography>
                <Typography
                  sx={{ fontSize: '0.7rem', color: C.muted, mt: 0.1 }}>
                  {(() => {
                    const p = (a.name.trim()[0] || 'T').toUpperCase();
                    return `${a.tableCount} table${
                      a.tableCount === 1 ? '' : 's'
                    } · ${p}-1–${p}-${a.tableCount}`;
                  })()}
                </Typography>
              </Box>
              {/* Stepper for table count (disabled once saved) */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#F9FAFB',
                  borderRadius: 5,
                  border: `1px solid ${C.border}`,
                  overflow: 'hidden',
                  opacity: a._synced ? 0.5 : 1,
                }}>
                <IconButton
                  size='small'
                  onClick={() => bumpCount(i, -1)}
                  disabled={a._synced || a.tableCount <= 1}
                  sx={{ width: 24, height: 24, color: C.danger, p: 0 }}>
                  −
                </IconButton>
                <Typography
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    minWidth: 26,
                    textAlign: 'center',
                  }}>
                  {a.tableCount}
                </Typography>
                <IconButton
                  size='small'
                  onClick={() => bumpCount(i, +1)}
                  disabled={a._synced}
                  sx={{ width: 24, height: 24, color: C.success, p: 0 }}>
                  +
                </IconButton>
              </Box>
              {a._synced && (
                <Box
                  sx={{
                    fontSize: '0.62rem',
                    fontWeight: 800,
                    color: C.success,
                    px: 0.7,
                    py: 0.15,
                    borderRadius: 0.8,
                    bgcolor: alpha(C.success, 0.12),
                    letterSpacing: 0.4,
                  }}>
                  SAVED
                </Box>
              )}
              <IconButton
                size='small'
                onClick={() => removeRow(i)}
                disabled={a._synced}
                sx={{ color: C.danger }}>
                <Close fontSize='small' />
              </IconButton>
            </Box>
          ))}
          <Typography
            sx={{
              mt: 0.5,
              fontSize: '0.74rem',
              color: C.muted,
              textAlign: 'right',
            }}>
            Total tables: <b>{totalTables}</b>
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

// ── Step 4 — Categories (menu sections) ──────────────────────────────────────
const CATEGORY_PRESETS = [
  'Starter',
  'Main Course',
  'Beverage',
  'Dessert',
  'Combo',
  'Pizza',
  'Ice Cream',
];

function CategoriesStep({ form, upd }) {
  const [name, setName] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const imgRef = useRef(null);

  const list = form.categories || [];

  const onImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const addRow = (n, file = null, preview = null) => {
    const clean = (n || '').trim();
    if (!clean) {
      toast.error('Category name is required');
      return;
    }
    if (
      list.some(
        (c) => String(c.name).trim().toLowerCase() === clean.toLowerCase(),
      )
    ) {
      toast.error(`"${clean}" is already added`);
      return;
    }
    upd('categories', [
      ...list,
      { name: clean, imageFile: file, imagePreview: preview, _synced: false },
    ]);
    setName('');
    setImageFile(null);
    setImagePreview(null);
    if (imgRef.current) imgRef.current.value = '';
  };

  const removeRow = (idx) => {
    upd(
      'categories',
      list.filter((_, i) => i !== idx),
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        border: `1px solid ${C.border}`,
        bgcolor: C.card,
      }}>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: '1.1rem',
          color: C.text,
          mb: 0.4,
          fontFamily: '"DM Sans", sans-serif',
        }}>
        Menu Categories
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', color: C.muted, mb: 2 }}>
        Group your items into sections. Images are optional and show up on the
        order screen.
      </Typography>

      {/* Quick-add chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 2 }}>
        {CATEGORY_PRESETS.map((p) => {
          const taken = list.some(
            (c) => String(c.name).trim().toLowerCase() === p.toLowerCase(),
          );
          return (
            <Box
              key={p}
              onClick={() => !taken && addRow(p)}
              sx={{
                px: 1.4,
                py: 0.6,
                borderRadius: 20,
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: taken ? 'default' : 'pointer',
                border: `1.5px solid ${taken ? C.border : C.primary}`,
                bgcolor: taken ? alpha(C.success, 0.08) : '#fff',
                color: taken ? C.success : C.primary,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.4,
                userSelect: 'none',
                opacity: taken ? 0.85 : 1,
              }}>
              {taken ? '✓' : '+'} {p}
            </Box>
          );
        })}
      </Box>

      {/* Custom add row with optional image */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size='small'
            label='Category name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addRow(name, imageFile, imagePreview);
            }}
          />
        </Grid>
        <Grid item xs={8} sm={4}>
          <Button
            fullWidth
            variant='outlined'
            onClick={() => imgRef.current?.click()}
            sx={{
              height: '100%',
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: C.border,
              color: imagePreview ? C.success : C.muted,
              justifyContent: 'flex-start',
            }}
            startIcon={
              imagePreview ? (
                <img
                  src={imagePreview}
                  alt=''
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <CloudUpload fontSize='small' />
              )
            }>
            {imagePreview ? 'Change image' : 'Image (optional)'}
          </Button>
          <input
            ref={imgRef}
            type='file'
            accept='image/*'
            hidden
            onChange={onImagePick}
          />
        </Grid>
        <Grid item xs={4} sm={2}>
          <Button
            fullWidth
            variant='outlined'
            disabled={!name.trim()}
            onClick={() => addRow(name, imageFile, imagePreview)}
            sx={{
              height: '100%',
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 700,
              borderColor: C.primary,
              color: C.primary,
            }}>
            Add
          </Button>
        </Grid>
      </Grid>

      {list.length === 0 ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(C.primary, 0.04),
            border: `1px dashed ${C.border}`,
            textAlign: 'center',
            color: C.muted,
            fontSize: '0.82rem',
          }}>
          No categories yet. Tap a preset or type one above.
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          {list.map((c, i) => (
            <Box
              key={`${c.name}-${i}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: `1px solid ${c._synced ? alpha(C.success, 0.5) : C.border}`,
                bgcolor: c._synced ? alpha(C.success, 0.05) : '#fff',
              }}>
              {/* Thumbnail (image or initial fallback) */}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: alpha(C.primary, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  overflow: 'hidden',
                  color: C.primary,
                  fontWeight: 800,
                  fontSize: '0.85rem',
                }}>
                {c.imagePreview ? (
                  <img
                    src={c.imagePreview}
                    alt={c.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  c.name.charAt(0).toUpperCase()
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.text }}>
                  {c.name}
                </Typography>
              </Box>
              {c._synced && (
                <Box
                  sx={{
                    fontSize: '0.62rem',
                    fontWeight: 800,
                    color: C.success,
                    px: 0.7,
                    py: 0.15,
                    borderRadius: 0.8,
                    bgcolor: alpha(C.success, 0.12),
                    letterSpacing: 0.4,
                  }}>
                  SAVED
                </Box>
              )}
              <IconButton
                size='small'
                onClick={() => removeRow(i)}
                disabled={c._synced}
                sx={{ color: C.danger }}>
                <Close fontSize='small' />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

// ── Step 5 — Items (menu items with prices) ──────────────────────────────────
// Each item references one category (by name → resolved to serverId at save)
// and optionally one department (by name string per the backend DTO). Fields
// the backend's CreateItem DTO doesn't accept (area, image, description,
// active status) are kept in local draft state for later screens.
function ItemsStep({ form, upd }) {
  const cats = form.categories || [];
  const depts = form.departments || [];
  const areas = form.areas || [];

  const [draft, setDraft] = useState({
    name: '',
    categoryName: cats[0]?.name || '',
    department: '',
    areaName: '',
    price: '',
    tax: 5,
    description: '',
    active: true,
  });

  const list = form.items || [];

  const updDraft = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const addRow = () => {
    if (!draft.name.trim()) {
      toast.error('Item name is required');
      return;
    }
    if (!draft.categoryName) {
      toast.error('Select a category for this item');
      return;
    }
    if (!(Number(draft.price) > 0)) {
      toast.error('Enter a price greater than 0');
      return;
    }
    if (
      list.some(
        (it) =>
          String(it.name).trim().toLowerCase() ===
            draft.name.trim().toLowerCase() &&
          it.categoryName === draft.categoryName,
      )
    ) {
      toast.error(`"${draft.name.trim()}" already exists in ${draft.categoryName}`);
      return;
    }
    upd('items', [
      ...list,
      {
        name: draft.name.trim(),
        categoryName: draft.categoryName,
        department: draft.department,
        areaName: draft.areaName, // local-only
        price: Number(draft.price),
        tax: Number(draft.tax) || 0,
        description: draft.description, // local-only
        active: draft.active, // local-only
        _synced: false,
      },
    ]);
    setDraft({
      name: '',
      categoryName: draft.categoryName,
      department: draft.department,
      areaName: draft.areaName,
      price: '',
      tax: draft.tax,
      description: '',
      active: true,
    });
  };

  const removeRow = (idx) => {
    upd(
      'items',
      list.filter((_, i) => i !== idx),
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        border: `1px solid ${C.border}`,
        bgcolor: C.card,
      }}>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: '1.1rem',
          color: C.text,
          mb: 0.4,
          fontFamily: '"DM Sans", sans-serif',
        }}>
        Menu Items
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', color: C.muted, mb: 2 }}>
        Add the dishes / drinks customers can order. Category and price are
        required; everything else is optional.
      </Typography>

      {cats.length === 0 && (
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: alpha(C.warn, 0.1),
            border: `1px solid ${alpha(C.warn, 0.4)}`,
            color: '#92400E',
            fontSize: '0.78rem',
          }}>
          Add at least one category in Step 4 first — items must belong to a
          category.
        </Box>
      )}

      {/* Add-item form */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            size='small'
            label='Item name'
            value={draft.name}
            onChange={(e) => updDraft('name', e.target.value)}
            placeholder='e.g. Paneer Butter Masala'
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            required
            size='small'
            label='Price'
            type='number'
            value={draft.price}
            onChange={(e) => updDraft('price', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>₹</InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            size='small'
            label='Tax %'
            type='number'
            value={draft.tax}
            onChange={(e) => updDraft('tax', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            required
            size='small'
            select
            label='Category'
            value={draft.categoryName}
            onChange={(e) => updDraft('categoryName', e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}>
            <option value=''>— Select —</option>
            {cats.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size='small'
            select
            label='Department'
            value={draft.department}
            onChange={(e) => updDraft('department', e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            disabled={depts.length === 0}>
            <option value=''>— None —</option>
            {depts.map((d) => (
              <option key={d.name} value={d.name}>
                {d.name}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size='small'
            select
            label='Area'
            value={draft.areaName}
            onChange={(e) => updDraft('areaName', e.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            disabled={areas.length === 0}>
            <option value=''>— Any —</option>
            {areas.map((a) => (
              <option key={a.name} value={a.name}>
                {a.name}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size='small'
            label='Description (optional)'
            value={draft.description}
            onChange={(e) => updDraft('description', e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            fullWidth
            variant='outlined'
            onClick={addRow}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderColor: C.primary,
              color: C.primary,
            }}>
            + Add item to menu
          </Button>
        </Grid>
      </Grid>

      {/* Added items list */}
      {list.length === 0 ? (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(C.primary, 0.04),
            border: `1px dashed ${C.border}`,
            textAlign: 'center',
            color: C.muted,
            fontSize: '0.82rem',
          }}>
          No items yet. Fill the form above and tap Add.
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
          {list.map((it, i) => (
            <Box
              key={`${it.name}-${i}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                border: `1px solid ${it._synced ? alpha(C.success, 0.5) : C.border}`,
                bgcolor: it._synced ? alpha(C.success, 0.05) : '#fff',
              }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{ fontWeight: 700, fontSize: '0.88rem', color: C.text }}>
                  {it.name}
                </Typography>
                <Typography
                  sx={{ fontSize: '0.72rem', color: C.muted, mt: 0.1 }}>
                  {[
                    it.categoryName,
                    it.department && `→ ${it.department}`,
                    it.areaName && `· ${it.areaName}`,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '0.88rem',
                    color: C.primary,
                  }}>
                  ₹{Number(it.price).toFixed(0)}
                </Typography>
                <Typography sx={{ fontSize: '0.62rem', color: C.muted }}>
                  +{Number(it.tax) || 0}% tax
                </Typography>
              </Box>
              {it._synced && (
                <Box
                  sx={{
                    fontSize: '0.62rem',
                    fontWeight: 800,
                    color: C.success,
                    px: 0.7,
                    py: 0.15,
                    borderRadius: 0.8,
                    bgcolor: alpha(C.success, 0.12),
                    letterSpacing: 0.4,
                  }}>
                  SAVED
                </Box>
              )}
              <IconButton
                size='small'
                onClick={() => removeRow(i)}
                disabled={it._synced}
                sx={{ color: C.danger }}>
                <Close fontSize='small' />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}
